import { createContext, useContext, useEffect, useState } from "react";
import request from "../config/request";
import {
    GoogleAuthProvider,
    signInWithPopup,
    GithubAuthProvider
} from "firebase/auth";
import { auth } from "../config/firebase-config";
import { useUserAuth } from "./UserAuthContext";

const MentorAuthContext = createContext();

export function MentorAuthContextProvider({ children }) {
    const { user, setUser, isNewUser, setIsNewUser, token, setToken, saveToken } = useUserAuth();

    function getTokenAndFetchUserProfile() {
        const token = localStorage.getItem("token");
        if (token) {
            setToken(token);
            fetchUserProfile(token);
        }
    }

    // Login with email/password
    async function logIn(email, password) {
        try {
            const res = await request.post("/api/mentor/auth/email/login", { email, password });
            if (res.data.token) {
                saveToken(res.data.token);
                await fetchUserProfile(res.data.token);
                return res.data;
            } else {
                throw new Error(res.data.message || "Login failed");
            }
        } catch (error) {
            throw error.response?.data || error;
        }
    }

    // Register new user
    async function signUp(email, password, name) {
        try {
            const res = await request.post("/api/mentor/auth/email/register", { name, email, password });
            if (res.data.response) {
                return res.data;
            } else {
                throw new Error(res.data.message || "Registration failed");
            }
        } catch (error) {
            throw error.response?.data || error;
        }
    }

    // Social login (Google/Github) with Firebase ID token
    async function socialSignIn(firebaseIdToken) {
        try {
            const res = await request.post("/api/mentor/auth/social/auth", { token: firebaseIdToken });
            if (res.data.token) {
                saveToken(res.data.token);
                setIsNewUser(!!res.data.isNewUser);
                await fetchUserProfile(res.data.token);
                return res.data;
            } else {
                throw new Error(res.data.message || "Social login failed");
            }
        } catch (error) {
            throw error.response?.data || error;
        }
    }

    // Get current user profile
    async function fetchUserProfile(jwt = token) {
        if (!jwt) return;
        try {
            const res = await request.get("/api/mentor/auth/protected/me");
            console.log(res);
            setUser(res.data);
        } catch (error) {
            setUser(null);
        }
    }

    // Password reset: Send password reset email via API
    async function forgetpassword(email) {
        try {
            const res = await request.post("/api/mentor/auth/forget-password", { email });
            if (res.data.response) {
                return res.data;
            } else {
                throw new Error(res.data.message || "Failed to send password reset email");
            }
        } catch (error) {
            throw error.response?.data || error;
        }
    }

    // Logout
    function logOut() {
        localStorage.removeItem("token");
        setToken(null);
        setUser(null);
        setIsNewUser(false);
        window.$crisp?.push?.(["do", "session:reset"]);
        return Promise.resolve();
    }

    // Google sign-in: get Firebase ID token and pass to socialSignIn
    async function googleSignIn() {
        const provider = new GoogleAuthProvider();
        provider.addScope('profile');
        provider.addScope('email');
        provider.setCustomParameters({ prompt: 'select_account' });
        const result = await signInWithPopup(auth, provider);
        console.log(result);
        const firebaseIdToken = await result.user.getIdToken();
        return socialSignIn(firebaseIdToken);
    }

    // Github sign-in: get Firebase ID token and pass to socialSignIn
    async function githubSignIn() {
        const provider = new GithubAuthProvider();
        provider.addScope('user');
        provider.addScope('user:email');
        provider.setCustomParameters({ allow_signup: 'true' });
        const result = await signInWithPopup(auth, provider);
        console.log(result);
        const firebaseIdToken = await result.user.getIdToken();
        return socialSignIn(firebaseIdToken);
    }

    const values = {
        user,
        isNewUser,
        logIn,
        signUp,
        logOut,
        forgetpassword,
        googleSignIn,
        githubSignIn,
        token,
        getTokenAndFetchUserProfile
    };

    return (
        <MentorAuthContext.Provider value={values}>
            {children}
        </MentorAuthContext.Provider>
    );
}

export function useMentorAuth() {
    return useContext(MentorAuthContext);
}

export default MentorAuthContextProvider;
